import { useCallback, useEffect, useRef, useState } from 'react';

export type PlaybackSpeed = 1 | 2 | 5 | 10 | 20 | 50 | 100 | 250 | 500 | 1000 | 2000;

const clampTime = (timeS: number, durationS: number): number =>
  Math.min(Math.max(0, Number.isFinite(timeS) ? timeS : 0), Math.max(0, durationS));

export const advanceSimulationTime = (
  currentTimeS: number,
  elapsedRealTimeS: number,
  playbackSpeed: PlaybackSpeed,
  durationS: number,
): number => clampTime(currentTimeS + Math.max(0, elapsedRealTimeS) * playbackSpeed, durationS);

export const useSimulationClock = (durationS: number) => {
  const safeDuration = Math.max(0, Number.isFinite(durationS) ? durationS : 0);
  const [currentTimeS, setCurrentTimeS] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeedState] = useState<PlaybackSpeed>(2000);
  const durationRef = useRef(safeDuration);
  const speedRef = useRef<PlaybackSpeed>(playbackSpeed);

  durationRef.current = safeDuration;
  speedRef.current = playbackSpeed;

  useEffect(() => {
    setCurrentTimeS((current) => clampTime(current, safeDuration));
    if (safeDuration <= 0) setIsPlaying(false);
  }, [safeDuration]);

  useEffect(() => {
    if (!isPlaying || durationRef.current <= 0) return undefined;
    let animationFrame = 0;
    let previousTimestamp: number | null = null;
    const tick = (timestamp: number) => {
      if (previousTimestamp === null) previousTimestamp = timestamp;
      const elapsedS = Math.max(0, timestamp - previousTimestamp) / 1000;
      previousTimestamp = timestamp;
      setCurrentTimeS((current) => {
        const next = advanceSimulationTime(current, elapsedS, speedRef.current, durationRef.current);
        if (next >= durationRef.current) {
          setIsPlaying(false);
          return durationRef.current;
        }
        return next;
      });
      animationFrame = requestAnimationFrame(tick);
    };
    animationFrame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrame);
  }, [isPlaying]);

  const play = useCallback(() => {
    if (durationRef.current <= 0) return;
    setCurrentTimeS((current) => current >= durationRef.current ? 0 : current);
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => setIsPlaying(false), []);

  const replay = useCallback(() => {
    setCurrentTimeS(0);
    setIsPlaying(durationRef.current > 0);
  }, []);

  const seek = useCallback((timeS: number) => {
    setCurrentTimeS(clampTime(timeS, durationRef.current));
  }, []);

  const setPlaybackSpeed = useCallback((speed: PlaybackSpeed) => {
    speedRef.current = speed;
    setPlaybackSpeedState(speed);
  }, []);

  return {
    currentTimeS,
    isPlaying,
    playbackSpeed,
    play,
    pause,
    replay,
    seek,
    setPlaybackSpeed,
  };
};
